import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

import { api, ApiError } from "../api/client";
import type { BusinessInfo, DayHours, DaypartKey } from "../api/types";
import { useLanguage } from "../i18n/LanguageContext";
import { DAY_KEYS, type DayKey } from "../lib/hours";
import "./BusinessEditor.css";

/**
 * Opening hours, address and contact — one small form, saved in one go.
 * A day without hours is a Ruhetag; a closing time before the opening time
 * (01:00) means "after midnight" and belongs to the day it opened.
 */

const TEXT_FIELDS: { key: "name" | "address" | "city" | "phone" | "email" | "mapsUrl" | "instagram"; label: "bizName" | "bizAddress" | "bizCity" | "phoneLabel" | "emailLabel" | "bizMaps" | "bizInstagram"; type?: string; wide?: boolean }[] = [
  { key: "name", label: "bizName" },
  { key: "phone", label: "phoneLabel", type: "tel" },
  { key: "address", label: "bizAddress" },
  { key: "city", label: "bizCity" },
  { key: "email", label: "emailLabel", type: "email" },
  { key: "mapsUrl", label: "bizMaps", type: "url", wide: true },
  { key: "instagram", label: "bizInstagram", type: "url", wide: true },
];

const DEFAULT_DAY: DayHours = { open: "09:00", close: "18:00" };

const DAYPARTS: { key: DaypartKey; label: "morningTitle" | "afternoonTitle" | "eveningTitle" }[] = [
  { key: "morning", label: "morningTitle" },
  { key: "afternoon", label: "afternoonTitle" },
  { key: "evening", label: "eveningTitle" },
];
const DEFAULT_DAYPARTS = { morning: { from: "09:00", to: "14:00" }, afternoon: { from: "14:00", to: "18:00" }, evening: { from: "18:00", to: "23:00" } };

export default function BusinessEditor() {
  const { t } = useLanguage();
  const [saved, setSaved] = useState<BusinessInfo | null>(null);
  const [draft, setDraft] = useState<BusinessInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bad, setBad] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const raw = await api.staff.business();
      const info = { ...raw, dayparts: { ...DEFAULT_DAYPARTS, ...(raw.dayparts ?? {}) } };
      setSaved(info);
      setDraft(structuredClone(info));
    } catch {
      setError(t("bizLoadError"));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(() => JSON.stringify(saved) !== JSON.stringify(draft), [saved, draft]);

  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [dirty]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setBad([]);
    try {
      const info = await api.staff.saveBusiness(draft);
      setSaved(info);
      setDraft(structuredClone(info));
      setFlash(true);
      setTimeout(() => setFlash(false), 2000);
    } catch (err) {
      if (err instanceof ApiError && err.fields.length) {
        setBad(err.fields);
        setError(t("bizInvalid"));
      } else {
        setError(err instanceof ApiError ? `${t("somethingWrong")} (${err.message})` : t("somethingWrong"));
      }
    } finally {
      setSaving(false);
    }
  };

  const patch = (change: Partial<BusinessInfo>) => setDraft((d) => (d ? { ...d, ...change } : d));

  const setDaypart = (key: DaypartKey, field: "from" | "to", value: string) =>
    setDraft((d) => (d ? { ...d, dayparts: { ...d.dayparts, [key]: { ...d.dayparts[key], [field]: value } } } : d));

  const setDay = (day: DayKey, hours: DayHours | null) =>
    setDraft((d) => {
      if (!d) return d;
      const next = { ...d.hours };
      if (hours) next[day] = hours;
      else delete next[day];
      return { ...d, hours: next };
    });

  if (error && !draft) {
    return (
      <main className="dash-main">
        <div className="dash-empty">
          <p>{error}</p>
          <button className="btn btn-outline btn-sm" onClick={load}>{t("retry")}</button>
        </div>
      </main>
    );
  }

  if (!draft) return <main className="dash-main"><div className="skeleton be-skeleton" /></main>;

  return (
    <main className="dash-main be">
      <div className="dash-head">
        <div className="dash-bar-title">
          <strong>{t("bizEditorTitle")}</strong>
          <span className="tiny">{t("bizEditorHint")}</span>
        </div>
      </div>

      {error && <p className="dash-hint">{error}</p>}

      <div className="be-grid">
        <section className="be-card">
          <h2 className="be-card-title">{t("hoursLabel")}</h2>
          <p className="tiny be-card-hint">{t("bizHoursHint")}</p>
          <ul className="be-days">
            {DAY_KEYS.map((day) => {
              const h = draft.hours[day];
              const invalid = bad.includes(`hours:${day}`);
              return (
                <li key={day} className={`be-day${h ? "" : " is-closed"}${invalid ? " is-invalid" : ""}`}>
                  <label className="be-day-toggle">
                    <input type="checkbox" checked={Boolean(h)} onChange={(e) => setDay(day, e.target.checked ? { ...DEFAULT_DAY } : null)} />
                    <span className="be-day-name">{t(day)}</span>
                  </label>
                  {h ? (
                    <div className="be-day-times">
                      <label className="be-time">
                        <span className="tiny">{t("bizOpens")}</span>
                        <input className="input nums" type="time" step={900} value={h.open} onChange={(e) => setDay(day, { ...h, open: e.target.value })} />
                      </label>
                      <span className="be-dash" aria-hidden="true">–</span>
                      <label className="be-time">
                        <span className="tiny">{t("bizCloses")}</span>
                        <input className="input nums" type="time" step={900} value={h.close} onChange={(e) => setDay(day, { ...h, close: e.target.value })} />
                      </label>
                    </div>
                  ) : (
                    <span className="be-closed-label">{t("closedLabel")}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        <section className="be-card">
          <h2 className="be-card-title">{t("bizDayparts")}</h2>
          <p className="tiny be-card-hint">{t("bizDaypartsHint")}</p>
          <ul className="be-days">
            {DAYPARTS.map(({ key, label }) => {
              const part = draft.dayparts[key];
              const invalid = bad.includes(`dayparts:${key}`);
              return (
                <li key={key} className={`be-day${invalid ? " is-invalid" : ""}`}>
                  <span className="be-day-name">{t(label)}</span>
                  <div className="be-day-times">
                    <label className="be-time">
                      <span className="tiny">{t("daypartFrom")}</span>
                      <input className="input nums" type="time" step={900} value={part.from} onChange={(e) => setDaypart(key, "from", e.target.value)} />
                    </label>
                    <span className="be-dash" aria-hidden="true">–</span>
                    <label className="be-time">
                      <span className="tiny">{t("daypartTo")}</span>
                      <input className="input nums" type="time" step={900} value={part.to} onChange={(e) => setDaypart(key, "to", e.target.value)} />
                    </label>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="be-card">
          <h2 className="be-card-title">{t("bizContact")}</h2>
          <div className="be-fields">
            {TEXT_FIELDS.map((f) => (
              <div key={f.key} className={`field${f.wide ? " be-wide" : ""}`}>
                <label className="label" htmlFor={`be-${f.key}`}>{t(f.label)}</label>
                <input
                  id={`be-${f.key}`}
                  className={`input${bad.includes(f.key) ? " is-invalid" : ""}`}
                  type={f.type ?? "text"}
                  value={draft[f.key]}
                  onChange={(e) => patch({ [f.key]: e.target.value })}
                />
              </div>
            ))}
          </div>
        </section>
      </div>

      <AnimatePresence>
        {(dirty || flash) && (
          <motion.div
            className="me-savebar"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 34 }}
          >
            <span className="me-savebar-text">{flash && !dirty ? `✓ ${t("savedOk")}` : t("unsavedChanges")}</span>
            {dirty && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={() => { setDraft(structuredClone(saved)); setBad([]); setError(null); }} disabled={saving}>
                  {t("discard")}
                </button>
                <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                  {saving ? t("sending") : t("saveChanges")}
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
